import React, { FC, useCallback, useEffect, useState } from "react";
import { Box } from "@mui/material";
import HomePageTitle from "@components/Home/HomePageTitle/HomePageTitle";
import Loader from "@components/Loader/Loader";
import TextIconButton from "@components/TextIconButton";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ConfirmDialog from "@components/ConfirmDialog/ConfirmDialog";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { useDebounceValue } from "@hooks/useDebounceValue";
import { useDeleteAlbumMutation, useGetAlbumsQuery } from "@store/albumApi";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@store/store";
import { Album } from "@models/album";
import AddIcon from "@mui/icons-material/Add";
import AlbumItem from "@components/AlbumItem";
import { setMessage } from "@store/snackbarSlice";
import { useNavigate } from "react-router-dom";
import EmptyState from '@components/EmptyState/EmptyState';

const Albums: FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const selectedPatientId = useSelector(
    (state: RootState) => state.home.selectedPatientId,
  );
  const searchForm = useForm<{ search: string }>();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [checkedAlbums, setCheckedAlbums] = useState<Set<Album>>(new Set());
  const searchStr = useDebounceValue(searchForm.watch("search"), 500);
  const { data: albums, isLoading } = useGetAlbumsQuery(
    { patientId: selectedPatientId!, search: searchStr },
    { skip: !selectedPatientId },
  );
  const [deleteAlbum, { isLoading: isDeleting }] = useDeleteAlbumMutation();

  useEffect(() => {
    setCheckedAlbums(new Set());
  }, []);

  const onDeleteSelectedAlbums = useCallback(async () => {
    setConfirmDialogOpen(false);

    if (!checkedAlbums?.size) {
      return;
    }

    for (const character of Array.from(checkedAlbums)) {
      const { error } = await deleteAlbum(character.id);

      if (error) {
        dispatch(
          setMessage({ content: t(`errors.unknown`), severity: "error" }),
        );
      }
    }
    setCheckedAlbums(new Set());
  }, [checkedAlbums, deleteAlbum, dispatch, t]);

  const onCheck = useCallback((album: Album) => {
    setCheckedAlbums((albums: Set<Album>) => {
      const newSet = new Set(albums);
      if (newSet.has(album)) {
        newSet.delete(album);
      } else {
        newSet.add(album);
      }
      return newSet;
    });
  }, []);

  return (
    <Box display="flex" flexDirection="column">
      <HomePageTitle
        title=""
        search={{
          placeholder: t("albums.searchInput.placeholder"),
          form: searchForm,
        }}
      />
      {!albums?.data || isLoading ? (
        <Loader />
      ) : (
        <>
          <Box
            marginBottom="32px"
            display="flex"
            justifyContent="space-between"
            gap="16px"
            alignItems="center"
          >
            <TextIconButton
              text={t("albums.deleteButton")}
              disabled={!checkedAlbums.size}
              variant="outlined"
              color="error"
              icon={DeleteOutlineOutlinedIcon}
              onClick={() => setConfirmDialogOpen(true)}
            />

            <TextIconButton
              text={t("albums.addAlbumButton")}
              variant="outlined"
              icon={AddIcon}
              onClick={() => navigate("new")}
            />
          </Box>

          {albums.data.length ? (
            <Box
              display="flex"
              gap="24px"
              flexWrap="wrap"
              justifyContent={{ xs: "center", sm: "flex-start" }}
            >
              {albums.data.map((album) => (
                <Box width={{ xs: "100%", sm: "290px" }} key={album.id}>
                  <AlbumItem
                    album={album}
                    onCheck={() => onCheck(album)}
                    checked={checkedAlbums.has(album)}
                  />
                </Box>
              ))}
            </Box>
          ) : (
            <EmptyState image={`${process.env.PUBLIC_URL}/images/art-albums.jpeg`} label={t('albums.noData')}/>
          )}

          <ConfirmDialog
            processing={isDeleting}
            open={confirmDialogOpen}
            onCancel={() => setConfirmDialogOpen(false)}
            onConfirm={onDeleteSelectedAlbums}
          />
        </>
      )}
    </Box>
  );
};

export default Albums;
